import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullStory } from '@fullstory/browser';

import { useBreakpoints } from 'hooks/useWindowSize';
import { Modal } from 'shared/components/Modal/Modal';
import { getPrimaryLanguage } from 'shared/utils/getPrimaryLanguage';
import { AgreementType } from 'api/types';
import { AutopayModalContent } from './AutopayModal.styles';
import { TermsAndConditionsModal } from './TermsAndConditionsModal';
import { AutopayCustomBankAccountModal } from './AutopayCustomBankAccountModal';

export enum AutoPayStep {
  TERMS_AND_CONDITIONS = 1,
  ADD_BANK_ACCOUNT = 2,
}

export type AutopayModalProps = {
  agreement: AgreementType;
  isOpen: boolean;
  initialStep: AutoPayStep;
  onCompleteFormModal: () => void;
  onCloseModal: () => void;
};

export function AutopayModal({
  agreement,
  isOpen,
  initialStep,
  onCompleteFormModal,
  onCloseModal,
}: AutopayModalProps) {
  const { t } = useTranslation();
  const screenSize = useBreakpoints();
  const [step, setStep] = useState<AutoPayStep>(initialStep);

  const achFailedVerification = !!agreement?.ach_elected && !agreement?.ach_verified;

  const handleCloseAutopayModal = useCallback(() => {
    FullStory('trackEvent', {
      name: `Onboarding - ACH Enrollment ${step === AutoPayStep.TERMS_AND_CONDITIONS ? 'T&C' : 'Form'}: Clicked Close`,
      properties: {
        page: 'Status',
        onboardingStep: 'AutoPay',
        onboardingMilestone: agreement.onboarding_milestone,
      },
    });

    onCloseModal();
  }, [agreement.onboarding_milestone, onCloseModal, step]);

  const handleClickAcceptTC = () => {
    FullStory('trackEvent', {
      name: 'Onboarding - ACH Enrollment T&C: Clicked Accept',
      properties: {
        page: 'Status',
        onboardingStep: 'AutoPay',
        onboardingMilestone: agreement.onboarding_milestone,
      },
    });
    setStep(AutoPayStep.ADD_BANK_ACCOUNT);
  };

  const handleClickDeclineTC = () => {
    FullStory('trackEvent', {
      name: 'Onboarding - ACH Enrollment T&C: Clicked Decline',
      properties: {
        page: 'Status',
        onboardingStep: 'AutoPay',
        onboardingMilestone: agreement.onboarding_milestone,
      },
    });
    handleCloseAutopayModal();
  };

  const handleSubmitForm = () => {
    FullStory('trackEvent', {
      name: `Onboarding - ACH ${achFailedVerification ? 'Re-Enrollment' : 'Enrollment'} Form: Clicked Submit`,
      properties: {
        page: 'Status',
        onboardingStep: 'AutoPay',
        onboardingMilestone: agreement.onboarding_milestone,
      },
    });
  };

  const renderModalContent = () => {
    if (step === AutoPayStep.TERMS_AND_CONDITIONS) {
      return (
        <TermsAndConditionsModal
          primaryLanguage={getPrimaryLanguage(agreement.primary_language)}
          onAccept={handleClickAcceptTC}
          onDecline={handleClickDeclineTC}
        />
      );
    }

    return (
      <AutopayCustomBankAccountModal
        agreementId={agreement.id}
        onClickSubmit={handleSubmitForm}
        onSuccess={onCompleteFormModal}
        onClickCancel={handleCloseAutopayModal}
      />
    );
  };

  return (
    <Modal
      contentLabel={t('autopayModal.neverMissABill')}
      styleVariant="tertiary"
      isOpen={isOpen}
      subHeader
      isFullWidth={screenSize === 'sm'}
      fitContent
      padding="24px"
      onRequestClose={handleCloseAutopayModal}
    >
      <AutopayModalContent>{renderModalContent()}</AutopayModalContent>
    </Modal>
  );
}

export default AutopayModal;
